<template>
  <div class="text-base font-normal leading-5">
    <div
      class="mb-2 flex justify-between gap-4 border-b border-solid border-black-10 pb-1.5 leading-6 md:pb-2"
    >
      <div v-if="displaySizeNote && sizeNote" class="flex gap-1">
        <IconMeasuringTape class="h-5 w-7 shrink-0" />
        <p class="text-base">
          {{ $t(sizeNote) }}
        </p>
      </div>
      <span v-else>{{ $t('pdp.size_picker.choose_size') }}:</span>
      <SizeGuide :hide-icon="displaySizeNote" />
    </div>

    <div class="product-size-picker__categories table">
      <div
        v-for="(sizeCategory, idx) in formatedSizeCategories"
        :key="idx"
        class="table-row"
      >
        <span class="table-cell h-6 whitespace-nowrap pr-2.5 leading-6">
          {{ $t(`pdp.size_picker.${sizeCategory.categoryName}`) }}:
        </span>
        <div class="table-cell">
          <SizeVariant
            v-for="size in sizeCategory.sizes"
            :key="size.id"
            :variant="size.variant"
            :active="selectedVariant && selectedVariant.id === size.variant.id"
            @select-size="handleSelectedSize"
          />
        </div>
      </div>
    </div>

    <LazyProductSizeGuideModal
      v-if="isProductSizeGuideModalOpen"
      :product="product"
    />
  </div>
</template>

<script setup lang="ts">
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { isInStock } from '@scayle/storefront-nuxt'
import SizeVariant from '~/components/product/pickers/SizeVariant.vue'
import SizeGuide from '~/components/product/pickers/SizeGuide.vue'
import useUiState from '~/composables/ui/useUiState'
import { getSizeNote } from '~/utils/product'

defineOptions({ name: 'ProductSizePicker' })

const props = defineProps<{
  product: Product
  variants: Variant[]
  selectedVariant?: Variant
  displaySizeNote?: boolean
}>()

const emit = defineEmits<{
  (e: 'preSelectSize' | 'selectSize', variant: Variant): void
}>()

const { setActiveVariant, isProductSizeGuideModalOpen } = useUiState()

const sizeNote = computed(() => getSizeNote(props.product))

const variantsInStock = computed<Variant[]>(() =>
  props.variants.filter((v) => isInStock(v)),
)

watch(
  variantsInStock,
  (newValue) => {
    if (Array.isArray(newValue) && newValue.length === 1) {
      const [variant] = variantsInStock.value
      if (!variant) return
      setActiveVariant(variant)
      emit('preSelectSize', variant)
      emit('selectSize', variant)
    } else {
      setActiveVariant(undefined)
    }
  },
  { immediate: true },
)

const sizeCategories = computed(() =>
  getProductSizesFromVariants(props.variants),
)

const handleSelectedSize = (variant: Variant): void => {
  emit('selectSize', variant)
}

const formatedSizeCategories = computed(() =>
  Object.entries(sizeCategories.value).map(([categoryName, sizes]) => ({
    categoryName,
    sizes,
  })),
)
</script>

<style lang="postcss" scoped>
.product-size-picker__categories {
  border-collapse: separate;
  border-spacing: 0 10px;
}
</style>
